 
import { environments } from "@/lib/config";
import { normalizeFileName, resolveContentType } from "@/lib/utils/file-format";
import { normalizePath, prependPublic, safeJoinUrl } from "@/lib/utils/path-utils";
import {
  extractS3UploadFiles,
  getDisplayFileNameFromFilePath,
} from "@/lib/utils/upload-file-mapping";
import { S3File } from "@/types/file-upload";

type PresignedPostsFields = {
  "Content-Type": string;
  key: string;
  "x-amz-algorithm": string;
  "x-amz-credential": string;
  "x-amz-date": string;
  policy: string;
  "x-amz-signature": string;
};

type PresignedPosts = {
  filepath: string;
  url: string;
  fields: PresignedPostsFields;
};

type RegisterS3ResponseData = {
  data: {
    expiration: number;
    failed_count: number;
    failed_posts: unknown[];
    presigned_posts: PresignedPosts[];
    total_requested: number;
    successful_count: number;
  };
  message_code: string;
  message: string;
  status: number;
  success: boolean;
};

const apiUrl = environments.restApiUrl;

export const uploadFiles = async (
  files: File[],
  expiration = 3600,
  prefixOrCustomFileName?: string,
  useAsCustomFileName = false
): Promise<S3File[]> => {
  if (!files?.length) return [];

  const safeNameMap: Record<string, string> = {};

  const s3ReqFilesRaw = useAsCustomFileName
    ? files.map((file) => {
      const contentType = resolveContentType(file.type);
      const prefix = normalizePath(prefixOrCustomFileName || "");
      const safeFileName = normalizeFileName(file.name);

      const finalPath = prefix ? `${prefix}/${safeFileName}` : safeFileName;
      const filepath = prependPublic(finalPath); // now safe: never starts with "/"

      safeNameMap[filepath] = safeFileName;

      return { filepath, content_type: contentType };
    })
    : extractS3UploadFiles(files, prefixOrCustomFileName);

  const s3ReqFiles = s3ReqFilesRaw.map((f) => {
    if ("_safeFileName" in f) {
      const safeName = f._safeFileName as string;
      safeNameMap[f.filepath] = safeName;
    }

    return {
      filepath: f.filepath,
      content_type: f.content_type,
    };
  });

  try {
    const s3Req = {
      files: s3ReqFiles,
      expiration,
    };

    const registerRes = await fetch(`${apiUrl}/upload/presigned-posts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(s3Req),
      credentials: "include",
    });

    if (!registerRes.ok) {
      const msg = await registerRes.text();
      throw new Error(`Failed to register with S3: ${msg}`);
    }

    const registerData: RegisterS3ResponseData = await registerRes.json();

    const enrichedPosts = registerData.data.presigned_posts.map((post) => {
      return {
        ...post,
        _safeFileName: safeNameMap[post.filepath] ?? null,
      };
    });

    const mapped = registerData.data.presigned_posts.map((item) => {
      return {
        file_path: item.filepath,
        file_url: safeJoinUrl(item.url, item.filepath), // always correct now
        file_name: getDisplayFileNameFromFilePath(item.filepath),
      };
    });

    // --- Upload loop ---
    for (let i = 0; i < enrichedPosts.length; i++) {
      const post = enrichedPosts[i];
      const file = files[i];

      if (!(file instanceof Blob)) {
        console.error(`Skipping upload: not a Blob/File -`, file);
        continue;
      }
      if (file.size === 0) {
        console.error(`Skipping upload: zero-size file ${file.name}`);
        continue;
      }

      const doUploadOnce = async () => {
        const formData = new FormData();
        Object.entries(post.fields).forEach(([k, v]) => formData.append(k, v));

        const safeFileName = post._safeFileName ?? file.name;
        formData.append("file", file, safeFileName);

        const res = await fetch(post.url, {
          method: "POST",
          body: formData,
        });

        if (!res.ok) {
          throw new Error(
            `Upload failed: ${res.status} ${res.statusText} - ${await res.text()}`
          );
        }

        return true;
      };

      try {
        // compute local hash for verification
        const localHash = await (async (b: Blob) => {
          const buf = await b.arrayBuffer();
          const h = await crypto.subtle.digest("SHA-256", buf);
          return Array.from(new Uint8Array(h))
            .map((n) => n.toString(16).padStart(2, "0"))
            .join("");
        })(file);

        // Try upload
        try {
          await doUploadOnce();
        } catch (err) {
          console.error(`Upload failed (1st) for ${file.name}:`, err);
          await doUploadOnce();
        }

        // Verification GET
        try {
          const fileUrl = safeJoinUrl(post.url, post.filepath);
          const verifyResp = await fetch(fileUrl);

          if (verifyResp.ok) {
            const remoteBlob = await verifyResp.blob();

            const remoteHash = await (async (b: Blob) => {
              const buf = await b.arrayBuffer();
              const h = await crypto.subtle.digest("SHA-256", buf);
              return Array.from(new Uint8Array(h))
                .map((n) => n.toString(16).padStart(2, "0"))
                .join("");
            })(remoteBlob);

            if (remoteBlob.size !== file.size || remoteHash !== localHash) {
              console.error("Verification mismatch", {
                localSize: file.size,
                remoteSize: remoteBlob.size,
                localHash,
                remoteHash,
              });

              await doUploadOnce();
            }
          } else {
            console.warn(
              `Verification GET failed for ${file.name}: ${verifyResp.status}`
            );
          }
        } catch (e) {
          console.warn(`Verification GET error for ${file.name}:`, e);
        }
      } catch (uploadErr) {
        console.error(`Error uploading ${file.name}:`, uploadErr);
      }
    }

    return mapped;
  } catch (error) {
    console.error("Upload failed:", error);
    return [];
  }
};
